import { useEffect, useState, type FormEvent } from 'react'
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Link,
} from '@mui/material'
import { Add as AddIcon, FolderOpen } from '@mui/icons-material'
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { appPath, DASHBOARD_HOME } from '@yourownmetric/shared'

import { useListProjectsQuery, useCreateProjectMutation } from '@/apis/projectsApi'
import { useAppDispatch } from '@/app/hooks'
import { selectProject } from '@/app/projectSlice'
import { trackUi } from '@/app/selfAnalytics'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'

export function ProjectsPage() {
    const { data: projects = [], isLoading } = useListProjectsQuery()
    const [createProject, { isLoading: isCreating }] = useCreateProjectMutation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [searchParams] = useSearchParams()
    // `/projects?new=1` (e.g. from the "Create project" empty state) opens the dialog
    const [showCreate, setShowCreate] = useState(searchParams.get('new') === '1')
    const [newApiKey, setNewApiKey] = useState<string | null>(null)
    const [name, setName] = useState('')
    const [domain, setDomain] = useState('')

    // Onboarding funnel (Y-APP): no project yet → project created, key shown → install next.
    const noProjects = !isLoading && projects.length === 0 && !newApiKey
    useEffect(() => {
        if (noProjects) {
            trackUi('onboarding_step_viewed', { step: 'create_project' })
        }
    }, [noProjects])
    useEffect(() => {
        if (newApiKey) {
            trackUi('onboarding_step_viewed', { step: 'install_snippet' })
        }
    }, [newApiKey])

    const handleCreate = async (e: FormEvent) => {
        e.preventDefault()
        try {
            const result = await createProject({ name, domain }).unwrap()
            // The backend returns apiKey on creation
            setNewApiKey(((result as unknown as Record<string, unknown>).apiKey as string) ?? null)
            dispatch(selectProject(result.id))
            setShowCreate(false)
            setName('')
            setDomain('')
        } catch {
            // handled by RTK Query
        }
    }

    const handleSelectProject = (projectId: string) => {
        dispatch(selectProject(projectId))
        navigate(DASHBOARD_HOME)
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5">Projects</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreate(true)}
                >
                    New Project
                </Button>
            </Box>

            {newApiKey && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNewApiKey(null)}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Project created! Here&apos;s your API key (shown once):
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontFamily: 'monospace',
                            bgcolor: 'grey.100',
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                            {newApiKey}
                        </Typography>
                        <CopyButton text={newApiKey} />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5 }}>
                        Next:{' '}
                        <Link component={RouterLink} to={appPath('docs')} underline="hover">
                            Follow the Integration Guide →
                        </Link>
                    </Typography>
                </Alert>
            )}

            {isLoading && <Typography>Loading projects…</Typography>}

            {!isLoading && projects.length === 0 && !newApiKey && (
                <EmptyState
                    icon={<FolderOpen />}
                    title="No projects yet"
                    description="Create your first project to start collecting analytics data."
                    actionLabel="Create Project"
                    onAction={() => setShowCreate(true)}
                />
            )}

            <Grid container spacing={2}>
                {projects.map(project => (
                    <Grid key={project.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card
                            sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                            onClick={() => handleSelectProject(project.id)}
                        >
                            <CardContent>
                                <Typography variant="h6">{project.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {project.domain}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Created {new Date(project.createdAt).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Create Project Dialog */}
            <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
                <Box component="form" onSubmit={handleCreate}>
                    <DialogTitle>Create New Project</DialogTitle>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            pt: '16px !important',
                        }}
                    >
                        <TextField
                            label="Project Name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. My Website"
                            fullWidth
                            required
                        />
                        <TextField
                            label="Domain"
                            value={domain}
                            onChange={e => setDomain(e.target.value)}
                            placeholder="e.g. example.com"
                            fullWidth
                            required
                            helperText="The domain where you'll install the tracking script"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!name || !domain || isCreating}
                        >
                            {isCreating ? 'Creating…' : 'Create Project'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    )
}
